import { Form } from '../ui/validated-form';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeft, ArrowRight, ArrowUpRight, Check, HelpCircle, Loader2 } from 'lucide-react';

/* Der Rechner steht im Hero der Aerzte-Seite, wo sonst eine Produktabbildung staende.
   Ein Bildschirmfoto zeigt, dass es die Software gibt. Der Rechner zeigt dem
   Arzt, was ihm gerade entgeht - mit seinen eigenen Zahlen.

   Warum in Schritten statt auf einmal: Mehrstufige Formulare wandeln im
   Schnitt 13,85 Prozent um gegen 4,53 bei einstufigen, und ein Fortschritts-
   balken allein bringt nochmal deutlich mehr. Der Grund ist Selbstbindung -
   wer Schritt eins beantwortet hat, beendet den Rest.

   Warum das Ergebnis frei sichtbar ist und erst danach nach Kontaktdaten
   gefragt wird: Eine Adressabfrage vor dem Ergebnis kostet 30 bis 50 Prozent
   der Abschluesse. Das Ergebnis erzeugt erst den Wunsch, der die Adresse wert
   ist.

   Voreingestellt mit echten Werten aus einer betreuten Praxis: 17 Prozent
   ausgereifte Beratungskonversion, 1.308 Euro durchschnittlicher Eingriff. */

const HOOK_URL = process.env.REACT_APP_LEAD_HOOK_URL || '';

const euro = (wert) => Math.round(wert).toLocaleString('de-DE', {
  style: 'currency', currency: 'EUR', maximumFractionDigits: 0,
});

/* Zaehlt eine Zahl hoch, sobald sie erscheint. Das Ergebnis ist der Moment,
   auf den die drei Schritte hinarbeiten - eine Zahl, die einfach dasteht,
   verschenkt ihn. Wer Bewegung reduziert haben moechte, bekommt sie sofort. */
function useHochzaehlen(ziel, aktiv) {
  const [wert, setWert] = useState(0);
  useEffect(() => {
    if (!aktiv) { setWert(0); return undefined; }
    const ruhig = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (ruhig) { setWert(ziel); return undefined; }
    let rahmen;
    const start = performance.now();
    const dauer = 900;
    const schritt = (jetzt) => {
      const anteil = Math.min(1, (jetzt - start) / dauer);
      const geglaettet = 1 - Math.pow(1 - anteil, 3);
      setWert(ziel * geglaettet);
      if (anteil < 1) rahmen = requestAnimationFrame(schritt);
    };
    rahmen = requestAnimationFrame(schritt);
    // Sicherheitsnetz: In einem Tab im Hintergrund feuert requestAnimationFrame
    // nicht. Ohne das stuende beim Zurueckwechseln eine Null statt des
    // Ergebnisses - ausgerechnet an der Stelle, auf die alles hinarbeitet.
    const notfall = setTimeout(() => setWert(ziel), dauer + 300);
    return () => { cancelAnimationFrame(rahmen); clearTimeout(notfall); };
  }, [ziel, aktiv]);
  return wert;
}

function Chips({ werte, aktiv, onWahl, einheit = '' }) {
  return (
    <div className="mt-3 flex flex-wrap gap-2">
      {werte.map((w) => (
        <button
          key={w}
          type="button"
          onClick={() => onWahl(w)}
          className={`h-9 rounded-full border px-3.5 text-sm tabular-nums transition ${
            aktiv === w
              ? 'border-[#CC3366] bg-[#CC3366] text-white shadow-[0_8px_20px_-12px_rgba(204,51,102,0.9)]'
              : 'border-[#E1E5EC] bg-white text-[#3B4962] hover:border-[#CC3366] hover:text-[#CC3366]'
          }`}
        >
          {w.toLocaleString('de-DE')}{einheit}
        </button>
      ))}
    </div>
  );
}

export default function ConversionCalculator() {
  const [schritt, setSchritt] = useState(0);
  const [beratungen, setBeratungen] = useState(20);
  const [quote, setQuote] = useState(17);
  const [wert, setWert] = useState(1308);
  const [unbekannt, setUnbekannt] = useState(false);

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [telefon, setTelefon] = useState('');
  const [einwilligung, setEinwilligung] = useState(false);
  const [sendet, setSendet] = useState(false);
  const [gesendet, setGesendet] = useState(false);
  const [fehler, setFehler] = useState('');

  const ziel = Math.min(quote + 13, 100);

  const { differenz, jahr } = useMemo(() => {
    const heute = beratungen * (quote / 100) * wert;
    const nachher = beratungen * (ziel / 100) * wert;
    // Das Jahr aus dem gerundeten Monatswert bilden. Sonst stuenden dort
    // 3.401 im Monat und 40.810 im Jahr, und wer nachrechnet findet eine
    // Abweichung - bei einer Zahl, die verkaufen soll, teurer als die
    // verlorene Nachkommastelle.
    const proMonat = Math.round(nachher - heute);
    return { differenz: proMonat, jahr: proMonat * 12 };
  }, [beratungen, quote, wert, ziel]);

  const animiert = useHochzaehlen(differenz, schritt >= 3);

  const absenden = useCallback(async (event) => {
    event.preventDefault();
    setFehler('');
    if (!einwilligung) { setFehler('Bitte stimme der Verarbeitung zu, damit wir antworten dürfen.'); return; }
    if (!HOOK_URL) { setFehler('Der Versand ist noch nicht eingerichtet. Bitte melde dich direkt bei uns.'); return; }
    setSendet(true);
    try {
      const antwort = await fetch(HOOK_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          submissionId: `rechner-${Date.now()}`,
          firstName: name.trim(),
          email: email.trim(),
          phone: telefon.trim(),
          campaign_name: 'Beratungskonversion-Rechner',
          beratungen_pro_monat: beratungen,
          konversion_prozent: unbekannt ? null : quote,
          behandlungswert: wert,
          potenzial_pro_monat: differenz,
        }),
      });
      if (!antwort.ok) throw new Error('Fehlgeschlagen');
      setGesendet(true);
    } catch {
      setFehler('Das hat gerade nicht geklappt. Versuche es bitte noch einmal.');
    } finally {
      setSendet(false);
    }
  }, [einwilligung, name, email, telefon, beratungen, quote, wert, unbekannt, differenz]);

  const feld = 'h-11 w-full rounded-xl border border-[#E1E5EC] bg-white px-3.5 text-[15px] text-[#0D2249] outline-none transition placeholder:text-[#8B95A6] focus:border-[#CC3366] focus:ring-2 focus:ring-[#CC3366]/15';
  const zahlFeld = 'h-11 w-28 rounded-xl border border-[#E1E5EC] bg-white px-3.5 text-right text-[15px] font-semibold tabular-nums text-[#0D2249] outline-none transition focus:border-[#CC3366] focus:ring-2 focus:ring-[#CC3366]/15';

  const schritte = ['Termine', 'Konversion', 'Wert', 'Ergebnis'];
  const fortschritt = Math.min(schritt, 3);

  return (
    <div
      className="mx-auto flex min-h-[430px] w-full max-w-[460px] flex-col rounded-[26px] border border-[#0D2249]/10 bg-white p-5 text-[#0D2249] shadow-[0_30px_80px_-40px_rgba(13,34,73,0.55)] md:p-6"
      data-testid="conversion-calculator"
    >
      <div className="flex items-center gap-1.5" aria-hidden="true">
        {schritte.map((name_, i) => (
          <span
            key={name_}
            className={`h-1.5 flex-1 rounded-full transition-colors ${i <= fortschritt ? 'bg-[#CC3366]' : 'bg-[#E9ECF2]'}`}
          />
        ))}
      </div>
      <p className="mt-3 text-xs font-semibold uppercase tracking-[0.14em] text-[#CC3366]">
        Schritt {fortschritt + 1} von 4 · {schritte[fortschritt]}
      </p>

      {schritt === 0 && (
        <div className="mt-4 flex flex-1 flex-col" data-testid="calc-step-0">
          <p className="text-[19px] font-semibold leading-snug">
            Wie viele Beratungstermine haben Sie im Monat?
          </p>
          <p className="mt-2 text-sm text-[#68748A]">Eine grobe Zahl reicht.</p>
          <Chips werte={[10, 20, 40, 80]} aktiv={beratungen} onWahl={setBeratungen} />
          <div className="mt-4 flex items-center gap-3">
            <span className="text-sm text-[#68748A]">oder genau</span>
            <input
              type="number" min="1" max="500" value={beratungen}
              onChange={(e) => setBeratungen(Math.max(1, Number(e.target.value) || 0))}
              className={zahlFeld} data-testid="calc-beratungen" aria-label="Beratungstermine im Monat"
            />
          </div>
        </div>
      )}

      {schritt === 1 && (
        <div className="mt-4 flex flex-1 flex-col" data-testid="calc-step-1">
          <p className="text-[19px] font-semibold leading-snug">
            Wie viele davon werden zu einer Behandlung?
          </p>
          <p className="mt-2 text-sm text-[#68748A]">
            Anteil der Beratungen, aus denen binnen 90 Tagen eine Behandlung wird.
          </p>
          <div className="mt-5 flex items-center justify-between">
            <span className="text-sm text-[#3B4962]">Ihr Anteil</span>
            <span className="flex h-11 w-28 items-center justify-end rounded-xl border border-[#E1E5EC] bg-[#F2F1EF] px-3.5 text-[15px] font-medium tabular-nums">
              {quote} %
            </span>
          </div>
          <input
            type="range" min="1" max="80" value={quote}
            onChange={(e) => { setQuote(Number(e.target.value)); setUnbekannt(false); }}
            className="mt-3 h-1.5 w-full cursor-pointer appearance-none rounded-full bg-[#E1E5EC] accent-[#CC3366]"
            data-testid="calc-quote" aria-label="Anteil der Beratungen, die zur Behandlung werden"
          />
          <button
            type="button"
            onClick={() => { setUnbekannt(true); setQuote(17); setSchritt(2); }}
            className="mt-4 inline-flex items-center gap-1.5 self-start text-sm text-[#CC3366] underline-offset-4 hover:underline"
            data-testid="calc-unknown"
          >
            <HelpCircle className="h-4 w-4" />
            Ich weiß es nicht
          </button>
          {unbekannt && (
            <p className="mt-3 rounded-2xl bg-[#FBE8EF] px-4 py-3 text-[13px] leading-relaxed text-[#3B4962]" data-testid="calc-unknown-note">
              <span className="font-semibold text-[#0D2249]">Damit sind Sie nicht allein.</span>{' '}
              Kaum eine Praxis kennt diese Zahl. Wir rechnen weiter mit 17 Prozent.
            </p>
          )}
        </div>
      )}

      {schritt === 2 && (
        <div className="mt-4 flex flex-1 flex-col" data-testid="calc-step-2">
          <p className="text-[19px] font-semibold leading-snug">
            Was bringt eine Behandlung im Schnitt ein?
          </p>
          <p className="mt-2 text-sm text-[#68748A]">Über alle Eingriffe gerechnet, ohne Nachsorge.</p>
          <Chips werte={[500, 1300, 2500, 5000]} aktiv={wert} onWahl={setWert} einheit=" €" />
          <div className="mt-4 flex items-center gap-3">
            <span className="text-sm text-[#68748A]">oder genau</span>
            <input
              type="number" min="1" max="100000" value={wert}
              onChange={(e) => setWert(Math.max(1, Number(e.target.value) || 0))}
              className={zahlFeld} data-testid="calc-wert" aria-label="Durchschnittlicher Behandlungswert in Euro"
            />
          </div>
        </div>
      )}

      {schritt >= 3 && (
        <div className="mt-4 flex flex-1 flex-col" data-testid="calc-result">
          <div className="rounded-[22px] bg-[#0D2249] p-5 text-white shadow-[0_22px_55px_-36px_rgba(13,34,73,0.9)]">
            <p className="text-xs uppercase tracking-[0.14em] text-[#F3A7C1]">
              Wenn 13 von 100 Beratungen mehr zur Behandlung werden
            </p>
            <p className="mt-2 text-[34px] font-semibold leading-none tabular-nums md:text-[40px]" data-testid="calc-diff">
              {euro(animiert)}
            </p>
            <p className="mt-1.5 text-sm text-white/70 tabular-nums">
              mehr im Monat · {euro(jahr)} im Jahr, ohne eine einzige zusätzliche Anfrage
            </p>
          </div>

          {gesendet ? (
            <div className="mt-4 flex flex-1 flex-col justify-center rounded-[22px] bg-[#FBE8EF] p-5 text-center" data-testid="calc-sent">
              <Check className="mx-auto h-8 w-8 text-[#CC3366]" />
              <p className="mt-3 font-semibold">Angekommen.</p>
              <p className="mt-1.5 text-sm leading-relaxed text-[#3B4962]">
                Sie hören in der Regel am selben Tag von uns. Für Ihre echte Zahl brauchen wir
                einen Export mit vier Spalten &ndash; keine Namen, keine Befunde.
              </p>
            </div>
          ) : (
            <Form className="mt-4 space-y-2.5" onSubmit={absenden} data-testid="calc-form">
              <p className="text-sm leading-relaxed text-[#3B4962]">
                <span className="font-semibold text-[#0D2249]">Das ist eine Schätzung.</span>{' '}
                Ihre echte Zahl rechnen wir aus Ihren Terminen aus, kostenlos.
              </p>
              <input
                type="text" value={name} onChange={(e) => setName(e.target.value)}
                placeholder="Name" required autoComplete="name"
                className={feld} data-testid="calc-name"
              />
              <input
                type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="E-Mail" required autoComplete="email"
                className={feld} data-testid="calc-email"
              />
              <input
                type="tel" value={telefon} onChange={(e) => setTelefon(e.target.value)}
                placeholder="Telefon (freiwillig)" autoComplete="tel"
                className={feld} data-testid="calc-phone"
              />
              <label className="flex items-start gap-2 pt-1 text-xs leading-relaxed text-[#68748A]">
                <input
                  type="checkbox" checked={einwilligung}
                  onChange={(e) => setEinwilligung(e.target.checked)}
                  className="mt-0.5 h-4 w-4 shrink-0 accent-[#CC3366]"
                  data-testid="calc-consent"
                />
                <span>
                  Meine Angaben dürfen zur Kontaktaufnahme verarbeitet werden.{' '}
                  <a href="/datenschutz" className="underline">Datenschutz</a>
                </span>
              </label>
              {fehler && <p className="text-xs text-[#9A2B24]" data-testid="calc-error">{fehler}</p>}
              <button
                type="submit" disabled={sendet}
                className="btn-shine inline-flex w-full items-center justify-center rounded-full bg-[#CC3366] px-6 py-3.5 text-[15px] font-semibold text-white transition hover:-translate-y-0.5 hover:bg-[#B52A5A] disabled:opacity-60"
                data-testid="calc-submit"
              >
                {sendet ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Meine echte Zahl anfordern<ArrowUpRight className="ml-2 h-4 w-4" /></>}
              </button>
            </Form>
          )}
        </div>
      )}

      <div className="mt-5 flex items-center justify-between gap-3">
        {schritt > 0 && !gesendet ? (
          <button
            type="button" onClick={() => setSchritt((s) => s - 1)}
            className="inline-flex items-center gap-1.5 text-sm text-[#68748A] hover:text-[#0D2249]"
            data-testid="calc-back"
          >
            <ArrowLeft className="h-4 w-4" />
            Zurück
          </button>
        ) : <span />}

        {schritt < 3 && (
          <button
            type="button" onClick={() => setSchritt((s) => s + 1)}
            className="btn-shine inline-flex items-center justify-center rounded-full bg-[#CC3366] px-6 py-3 text-[15px] font-semibold text-white transition hover:-translate-y-0.5 hover:bg-[#B52A5A]"
            data-testid="calc-next"
          >
            {schritt === 2 ? 'Ergebnis zeigen' : 'Weiter'}
            <ArrowRight className="ml-2 h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );
}
